import countries from "i18n-iso-countries";
import {
  ADDRESS_LINE_MAX_LENGTH,
  CITY_FIELD_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  sanitizeAddressLineInput,
  sanitizePostalCodeInput,
  toTwoLetterStateCode,
} from "./addressFieldValidation";
import { sanitizeCityInput } from "./personNameValidation";

export type ParsedSenderAddress = {
  line1: string;
  line2: string;
  city: string;
  zip: string;
  state: string;
  /** ISO3 country code when place details include a country. */
  country: string;
};

/** Normalize AWS / place country codes to ISO3 (e.g. US -> USA). */
function toIso3CountryCode(raw?: string): string {
  const upper = String(raw ?? "").trim().toUpperCase();
  if (!upper) return "";
  if (upper.length === 3) return upper;
  if (upper.length === 2) {
    return countries.alpha2ToAlpha3(upper) ?? upper;
  }
  return upper;
}

/**
 * AWS Place.Region is often the full name ("Ontario"). Suggestion / Label text
 * usually has the abbreviation ("ON"). Prefer a 2-letter comma segment that is
 * not the country code.
 *
 * Example: "123 Teston Rd, Woodbridge, ON, L3L 0B5, CAN"
 */
function abbreviationFromCommaText(
  text: string,
  countryTokens: Set<string>
): string {
  const parts = String(text || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const part of parts) {
    const upper = part.toUpperCase();
    if (/^[A-Z]{2}$/.test(upper) && !countryTokens.has(upper)) {
      return upper;
    }
  }
  return "";
}

/**
 * Prefer a 2-letter subdivision code from Region / Label / suggestion text.
 * If none is found, return "" — do not fall back to a full region name
 * (updateConsumerProfile only accepts 2-letter senderState).
 */
function subdivisionCodeFromPlace(
  label: string,
  suggestionText: string,
  region: string,
  countryIso3: string
): string {
  const fromRegion = toTwoLetterStateCode(region);
  if (fromRegion) return fromRegion;

  const countryIso2 = countryIso3
    ? countries.alpha3ToAlpha2(countryIso3) ?? ""
    : "";
  const countryTokens = new Set(
    [countryIso3, countryIso2, "USA", "US", "CAN", "CA"]
      .filter(Boolean)
      .map((c) => c.toUpperCase())
  );

  return (
    abbreviationFromCommaText(label, countryTokens) ||
    abbreviationFromCommaText(suggestionText, countryTokens) ||
    ""
  );
}

export type ParsePlaceDetailsOptions = {
  /** Suggestion `Text` from search; used when Place.Label lacks the abbr. */
  suggestionText?: string;
};

/** Map AWS Location `GetPlace` payload into send-flow address fields. */
export function parsePlaceDetailsForSenderAddress(
  place: Record<string, unknown> | null | undefined,
  options?: ParsePlaceDetailsOptions
): ParsedSenderAddress {
  const p = place ?? {};
  const rawLine1 =
    `${String(p.AddressNumber ?? "")} ${String(p.Street ?? "")}`.trim() ||
    String(p.Label ?? "")
      .split(",")[0]
      ?.trim() ||
    "";

  const line2Raw = String(
    p.Unit ?? p.AddressNumber2 ?? p.SubAddress ?? p.Building ?? ""
  ).trim();

  const rawCity =
    String(p.Municipality ?? "").slice(0, CITY_FIELD_MAX_LENGTH) ||
    String(p.Neighborhood ?? "").slice(0, CITY_FIELD_MAX_LENGTH) ||
    "";

  const rawZip = String(p.PostalCode ?? "");
  const country = toIso3CountryCode(String(p.Country ?? "")) || "USA";
  const region = String(p.Region ?? "").trim();
  const rawState = subdivisionCodeFromPlace(
    String(p.Label ?? ""),
    String(options?.suggestionText ?? ""),
    region,
    country
  );

  return {
    line1: sanitizeAddressLineInput(rawLine1).slice(0, ADDRESS_LINE_MAX_LENGTH),
    line2: sanitizeAddressLineInput(line2Raw).slice(0, ADDRESS_LINE_MAX_LENGTH),
    city: sanitizeCityInput(rawCity, CITY_FIELD_MAX_LENGTH),
    zip: sanitizePostalCodeInput(rawZip),
    state: toTwoLetterStateCode(rawState),
    country,
  };
}
